import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Skeleton,
  Chip,
  alpha,
  Divider,
  Alert,
  LinearProgress,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useClients } from "../../hooks/useClients";
import { useConversations } from "../../hooks/useConversations";
import { useTypes } from "../../hooks/useTypes";
import { useChannels } from "../../hooks/useChannels";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import FilterChipsCollapsible from "../../components/FilterChipsCollapsible";
import SearchResultsBulkBar from "../../components/search/SearchResultsBulkBar";
import MergeRequestsDialog from "../../components/search/MergeRequestsDialog";
import DistributeRequestsDialog from "../../components/search/DistributeRequestsDialog";
import BulkChangeRequestsDialog from "../../components/search/BulkChangeRequestsDialog";
import { useSearchResultsBulkPermissions } from "../../hooks/useSearchResultsBulkPermissions";
import {
  parseSearchParamsFromUrl,
  TAG_NO_TAG,
  shouldAutoIncludeMessageTextSearch,
  searchResultsTabToIndex,
  searchResultsIndexToTab,
  SEARCH_RESULTS_TAB_DEFAULT,
  SEARCH_CONVERSATIONS_PAGE_SIZE,
  formatSearchResultsTabCount,
  canEnableAllPeriod,
} from "../../utils/searchFilterState";
import { useDataGridLocaleText } from "../../i18n/useDataGridLocale";
import ClientRelationshipBadge from "../../components/Chat/ClientRelationshipBadge";
import { getClientListRelationshipProps } from "../../utils/clientRelationships";

function flattenTypesTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => [
    { id: n.id, id_pai: n.id_pai ?? null, nome: n.nome || String(n.id) },
    ...flattenTypesTree(n.children || []),
  ]);
}

function SearchResultsTabLabel({ name, countLabel }) {
  const loaded = countLabel != null;
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}
    >
      {name}
      {loaded ? (
        <Box component="span">({countLabel})</Box>
      ) : (
        <Box
          component="span"
          title="Abra a aba para carregar os resultados"
          sx={{
            color: "text.disabled",
            fontWeight: 400,
            letterSpacing: 0.2,
            lineHeight: 1,
          }}
          aria-label="Ainda não carregado"
        >
          …
        </Box>
      )}
    </Box>
  );
}

function formatDateBR(dateStr) {
  if (!dateStr) return "";
  // Se já estiver em formato brasileiro, mantém
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // Converte de ISO (YYYY-MM-DD) para DD/MM/YYYY
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }
  // Fallback: tenta usar Date nativo
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("pt-BR");
  }
  return dateStr;
}

function getSafeDate(valueCandidates) {
  const raw = valueCandidates.find((v) => v != null && v !== "") ?? null;
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** Rótulo do responsável (agente de IA > usuário > Fila), alinhado à lista de chat. */
function getConversationAssigneeLabel(row, { userIdToName = {}, agentIdToName = {} } = {}) {
  if (!row) return "Fila";

  const rawAgentId =
    row.agentId ?? row.id_agente ?? row.agenteId ?? row.idAgent ?? null;
  const hasAgent =
    rawAgentId !== null &&
    rawAgentId !== undefined &&
    rawAgentId !== "" &&
    rawAgentId !== 0 &&
    rawAgentId !== "0";

  if (hasAgent) {
    return (
      row.agentName ||
      row.agent_name ||
      agentIdToName[String(rawAgentId)] ||
      "Agente de IA"
    );
  }

  const assignedToValue = row.assignedTo ?? row.userName ?? null;
  const assignedIsQueueLabel =
    typeof assignedToValue === "string" &&
    ["fila", "queue"].includes(assignedToValue.toLowerCase());

  if (
    assignedToValue != null &&
    assignedToValue !== "" &&
    assignedToValue !== 0 &&
    assignedToValue !== "0" &&
    !assignedIsQueueLabel
  ) {
    return String(assignedToValue);
  }

  const userId = row.userId ?? row.id_usuario ?? null;
  if (userId != null && userId !== "" && userId !== 0 && userId !== "0") {
    return userIdToName[String(userId)] || `Usuário ${userId}`;
  }

  return "Fila";
}

const defaultConversationPagination = () => ({
  page: 0,
  pageSize: SEARCH_CONVERSATIONS_PAGE_SIZE,
});

const SearchResults = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [paginationRel, setPaginationRel] = useState(defaultConversationPagination);
  const [paginationCom, setPaginationCom] = useState(defaultConversationPagination);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dataGridLocaleText = useDataGridLocaleText();
  const { canBulk, canExport } = useSearchResultsBulkPermissions();
  const showConversationSelection = canBulk || canExport;

  const {
    q,
    filters,
    data_inicio,
    data_fim,
    date_field,
    tipo_responsavel,
    ids_tipos,
    ids_subtipo1,
    ids_subtipo2,
    ids_subtipo3,
    ids_origens,
    ids_usuarios,
    ids_usuarios_interacao,
    ids_agentes,
    ids_agentes_interacao,
    tags,
    include_message_text,
    all_period,
    tab,
  } = useMemo(
    () => parseSearchParamsFromUrl(location.search),
    [location.search]
  );

  const tabFromUrl = useMemo(() => searchResultsTabToIndex(tab), [tab]);

  useEffect(() => {
    setTabIndex(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    setPaginationRel(defaultConversationPagination());
    setPaginationCom(defaultConversationPagination());
    setSelectedConversationIds([]);
  }, [location.search]);

  const qTrim = useMemo(() => (typeof q === "string" ? q.trim() : ""), [q]);

  const includeMessageSearchActive = useMemo(() => {
    const fromUrl =
      include_message_text === "1" ||
      include_message_text === "true" ||
      include_message_text === "sim" ||
      include_message_text === true;
    return fromUrl || shouldAutoIncludeMessageTextSearch(qTrim);
  }, [include_message_text, qTrim]);

  useEffect(() => {
    if (!shouldAutoIncludeMessageTextSearch(qTrim)) return;
    const inUrl =
      include_message_text === "1" ||
      include_message_text === "true" ||
      include_message_text === "sim";
    if (inUrl) return;
    const params = new URLSearchParams(location.search);
    params.set("include_message_text", "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [qTrim, include_message_text, location.pathname, location.search, navigate]);

  const { data: typesData } = useTypes(1, 500, "");
  const { data: channelsData } = useChannels(1, 200, "");
  // Inclui ChatBots (ura=1) para os nomes aparecerem corretamente quando o filtro de responsável incluir bots.
  const { data: usersData } = useUsers(1, 1000, "", "todos", { order: "nome" });
  const { data: operatorsData } = useUsers(1, 1000, "", "humanos", { order: "nome" });
  const { data: agentsData } = useAgents(1, 200, "");

  const typesFlat = useMemo(
    () => flattenTypesTree(typesData?.types || []),
    [typesData?.types]
  );
  const typeIdToName = useMemo(
    () => Object.fromEntries(typesFlat.map((t) => [String(t.id), t.nome || `Tipo ${t.id}`])),
    [typesFlat]
  );
  const channelIdToName = useMemo(
    () =>
      Object.fromEntries(
        (channelsData?.channels || []).map((c) => [String(c.id), c.nome || c.name || `Canal ${c.id}`])
      ),
    [channelsData?.channels]
  );
  const userIdToName = useMemo(
    () =>
      Object.fromEntries(
        (usersData?.users || []).map((u) => [String(u.id), u.nome || u.name || `Usuário ${u.id}`])
      ),
    [usersData?.users]
  );
  const agentIdToName = useMemo(
    () =>
      Object.fromEntries(
        (agentsData?.agents || []).map((a) => {
          const id = a.id ?? a.id_agente ?? a.agentId;
          return [String(id), a.name || a.nome || `Agente ${id}`];
        })
      ),
    [agentsData?.agents]
  );

  const updateUrl = (updates) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || (Array.isArray(value) && value.length === 0)) params.delete(key);
      else if (Array.isArray(value)) params.set(key, value.join(","));
      else params.set(key, value);
    });
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const removeFilter = (key, valueToRemove) => {
    if (key === "q") {
      updateUrl({ q: "" });
    } else if (key === "filters") {
      const next = filters.filter((f) => f !== valueToRemove);
      const updates = { filters: next };
      if (all_period && !canEnableAllPeriod(next)) {
        updates.all_period = "";
      }
      updateUrl(updates);
    } else if (key === "data_inicio" || key === "data_fim") {
      updateUrl({ [key]: "" });
    } else if (key === "all_period") {
      updateUrl({ all_period: "" });
    } else if (key === "ids_tipos") {
      const next = ids_tipos.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_tipos: next });
    } else if (key === "ids_subtipo1") {
      const next = ids_subtipo1.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_subtipo1: next });
    } else if (key === "ids_subtipo2") {
      const next = ids_subtipo2.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_subtipo2: next });
    } else if (key === "ids_subtipo3") {
      const next = ids_subtipo3.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_subtipo3: next });
    } else if (key === "ids_origens") {
      const next = ids_origens.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_origens: next });
    } else if (key === "ids_usuarios") {
      const next = ids_usuarios.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_usuarios: next });
    } else if (key === "ids_usuarios_interacao") {
      const next = ids_usuarios_interacao.filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_usuarios_interacao: next });
    } else if (key === "ids_agentes") {
      const next = (ids_agentes || []).filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_agentes: next });
    } else if (key === "ids_agentes_interacao") {
      const next = (ids_agentes_interacao || []).filter((id) => String(id) !== String(valueToRemove));
      updateUrl({ ids_agentes_interacao: next });
    } else if (key === "tags") {
      const next = tags.filter((t) => String(t) !== String(valueToRemove));
      updateUrl({ tags: next });
    } else if (key === "date_field") {
      updateUrl({ date_field: "entrada" });
    } else if (key === "tipo_responsavel") {
      updateUrl({ tipo_responsavel: "" });
    } else if (key === "include_message_text") {
      updateUrl({ include_message_text: "" });
    }
  };

  const advancedParams = useMemo(() => {
    const hasDates = !all_period && (data_inicio || data_fim);
    const hasIds =
      (ids_tipos?.length || 0) +
      (ids_subtipo1?.length || 0) +
      (ids_subtipo2?.length || 0) +
      (ids_subtipo3?.length || 0) +
      (ids_origens?.length || 0) +
      (ids_usuarios?.length || 0) +
      (ids_usuarios_interacao?.length || 0) +
      (ids_agentes?.length || 0) +
      (ids_agentes_interacao?.length || 0) +
      (tags?.length || 0) >
      0;
    const hasTipoResponsavel =
      ["fila", "humanos", "chatbots", "agentes"].includes(tipo_responsavel);

    if (!hasDates && !hasIds && !hasTipoResponsavel && !includeMessageSearchActive && !all_period) {
      return null;
    }

    return {
      ...(hasDates ? { data_inicio, data_fim, date_field } : {}),
      ...(ids_tipos?.length ? { ids_tipos } : {}),
      ...(ids_subtipo1?.length ? { ids_subtipo1 } : {}),
      ...(ids_subtipo2?.length ? { ids_subtipo2 } : {}),
      ...(ids_subtipo3?.length ? { ids_subtipo3 } : {}),
      ...(ids_origens?.length ? { ids_origens } : {}),
      ...(ids_usuarios?.length ? { ids_usuarios } : {}),
      ...(ids_usuarios_interacao?.length ? { ids_usuarios_interacao } : {}),
      ...(ids_agentes?.length ? { ids_agentes } : {}),
      ...(ids_agentes_interacao?.length ? { ids_agentes_interacao } : {}),
      ...(tags?.length ? { tags } : {}),
      ...(hasTipoResponsavel ? { tipo_responsavel } : {}),
      ...(includeMessageSearchActive ? { include_message_text: true } : {}),
    };
  }, [
    all_period,
    data_inicio,
    data_fim,
    date_field,
    tipo_responsavel,
    ids_tipos,
    ids_subtipo1,
    ids_subtipo2,
    ids_subtipo3,
    ids_origens,
    ids_usuarios,
    ids_usuarios_interacao,
    ids_agentes,
    ids_agentes_interacao,
    tags,
    includeMessageSearchActive,
  ]);

  const advancedParamsRel = useMemo(
    () => ({
      ...(advancedParams || {}),
      tipo_scope: "relacionamento",
      include_restricted: true,
      default_date_window_days: all_period
        ? 0
        : advancedParams?.data_inicio || advancedParams?.data_fim
          ? 0
          : 90,
      limit: 50,
    }),
    [advancedParams, all_period]
  );
  const advancedParamsCom = useMemo(
    () => ({
      ...(advancedParams || {}),
      tipo_scope: "comercial",
      include_restricted: true,
      default_date_window_days: all_period
        ? 0
        : advancedParams?.data_inicio || advancedParams?.data_fim
          ? 0
          : 90,
      limit: 50,
    }),
    [advancedParams, all_period]
  );

  const isAtendimentoTab = tabIndex === 0;
  const isComprasTab = tabIndex === 1;
  const isClientesTab = tabIndex === 2;

  const shouldFetchConversations = true;

  const { data: clientData, isLoading: loadingClients, isFetched: clientsFetched } = useClients(
    1,
    10,
    q,
    null,
    { enabled: isClientesTab }
  );

  const {
    data: conversationsDataRel,
    isLoading: loadingConversationsRel,
    isFetchingNextPage: isFetchingNextPageRel,
    fetchNextPage: fetchNextPageRel,
    hasNextPage: hasNextPageRel,
    isFetched: conversationsRelFetched,
  } = useConversations(q, filters, advancedParamsRel, false, {
    enabled: isAtendimentoTab && shouldFetchConversations,
  });

  const {
    data: conversationsDataCom,
    isLoading: loadingConversationsCom,
    isFetchingNextPage: isFetchingNextPageCom,
    fetchNextPage: fetchNextPageCom,
    hasNextPage: hasNextPageCom,
    isFetched: conversationsComFetched,
  } = useConversations(q, filters, advancedParamsCom, false, {
    enabled: isComprasTab && shouldFetchConversations,
  });

  useEffect(() => {
    if (!isAtendimentoTab) return;
    const neededPages = paginationRel.page + 1;
    const fetchedPages = conversationsDataRel?.pages?.length || 0;
    if (neededPages > fetchedPages && hasNextPageRel && !isFetchingNextPageRel) {
      fetchNextPageRel();
    }
  }, [
    isAtendimentoTab,
    paginationRel.page,
    conversationsDataRel?.pages?.length,
    hasNextPageRel,
    isFetchingNextPageRel,
    fetchNextPageRel,
  ]);

  useEffect(() => {
    if (!isComprasTab) return;
    const neededPages = paginationCom.page + 1;
    const fetchedPages = conversationsDataCom?.pages?.length || 0;
    if (neededPages > fetchedPages && hasNextPageCom && !isFetchingNextPageCom) {
      fetchNextPageCom();
    }
  }, [
    isComprasTab,
    paginationCom.page,
    conversationsDataCom?.pages?.length,
    hasNextPageCom,
    isFetchingNextPageCom,
    fetchNextPageCom,
  ]);

  const handleTabChange = (_, newIndex) => {
    setTabIndex(newIndex);
    setSelectedConversationIds([]);
    const nextTab = searchResultsIndexToTab(newIndex);
    updateUrl({ tab: nextTab === SEARCH_RESULTS_TAB_DEFAULT ? "" : nextTab });
  };

  const refreshConversations = () => {
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
    setSelectedConversationIds([]);
  };

  const notifyBulkSuccess = (message) => {
    setSnackbar({ open: true, message, severity: "success" });
    refreshConversations();
  };

  const isInteractiveGridTarget = (event) =>
    !!event.target.closest?.(
      'button, a, input, textarea, select, [role="button"], .MuiCheckbox-root, .MuiIconButton-root'
    );

  const renderConversationGrid = ({
    currentPageRows,
    allLoadedRows,
    paginationModel,
    onPaginationModelChange,
    loadedCount,
    hasNextPage,
    isFetchingNextPage,
    exportFileName,
    rowNavigatePath = (id) => `/chat/${id}`,
  }) => {
    const rowCount = hasNextPage ? -1 : loadedCount;
    const tablePaginationLocale = {
      ...dataGridLocaleText.MuiTablePagination,
      labelDisplayedRows: ({ from, to, count }) => {
        if (count === -1) {
          return `${from}–${to} de ${to}+`;
        }
        return `${from}–${to} de ${count}`;
      },
    };

    return (
      <>
        {showConversationSelection && (
          <SearchResultsBulkBar
            selectedCount={selectedConversationIds.length}
            selectedIds={selectedConversationIds}
            rows={allLoadedRows}
            columns={conversationColumns}
            exportTitle="Resultados da Busca"
            exportFileName={exportFileName}
            exportContext={{ typeIdToName }}
            canBulk={canBulk}
            canExport={canExport}
            onMerge={() => canBulk && setMergeOpen(true)}
            onDistribute={() => canBulk && setDistributeOpen(true)}
            onChangeStatus={() => canBulk && setChangeStatusOpen(true)}
            onClearSelection={() => setSelectedConversationIds([])}
          />
        )}
        <Paper elevation={0} variant="outlined" sx={{ flex: 1, minHeight: 400 }}>
          <DataGrid
            rows={currentPageRows}
            columns={conversationColumns}
            autoHeight
            checkboxSelection={showConversationSelection}
            disableRowSelectionOnClick
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={[SEARCH_CONVERSATIONS_PAGE_SIZE]}
            rowCount={rowCount}
            estimatedRowCount={hasNextPage ? loadedCount : undefined}
            paginationMeta={{ hasNextPage: Boolean(hasNextPage) }}
            loading={isFetchingNextPage}
            localeText={{
              ...dataGridLocaleText,
              MuiTablePagination: tablePaginationLocale,
            }}
            {...(showConversationSelection
              ? {
                  rowSelectionModel: selectedConversationIds,
                  onRowSelectionModelChange: (newSelection) =>
                    setSelectedConversationIds(newSelection),
                }
              : {})}
            isRowSelectable={(params) => !params.row?.restricted}
            getRowClassName={(params) =>
              params.row?.restricted ? "restricted-lead" : ""
            }
            onRowClick={(params, event) => {
              if (params.row?.restricted) return;
              if (isInteractiveGridTarget(event)) return;
              navigate(rowNavigatePath(params.row.id));
            }}
            onCellClick={(params, event) => {
              if (params.row?.restricted) return;
              if (params.field === "__check__") return;
              if (isInteractiveGridTarget(event)) return;
              if (params.field === "actions") return;
              navigate(rowNavigatePath(params.row.id));
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": { py: 1 },
              "& .MuiDataGrid-columnHeaders": { minHeight: 44 },
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-row.restricted-lead": {
                cursor: "default",
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
              },
            }}
          />
        </Paper>
      </>
    );
  };

  const sanitizeText = (text) => {
    if (!text) return "—";
    const clean = String(text).replace(/<[^>]*>/g, "").trim();
    return clean || "—";
  };

  const conversationColumns = [
    {
      field: "id",
      headerName: "Protocolo",
      width: 110,
      minWidth: 88,
      valueGetter: (value, row) => (row?.restricted ? "••••" : value),
    },
    {
      field: "name",
      headerName: "Nome",
      minWidth: 160,
      flex: 0.5,
      valueGetter: (value, row) => (row?.restricted ? "—" : value),
    },
    {
      field: "assignee",
      headerName: "Responsável",
      minWidth: 140,
      flex: 0.4,
      valueGetter: (_, row) =>
        row?.restricted
          ? "—"
          : getConversationAssigneeLabel(row, { userIdToName, agentIdToName }),
    },
    {
      field: "lastMessage",
      headerName: "Última Mensagem",
      minWidth: 200,
      flex: 1,
      valueGetter: (value, row) =>
        row?.restricted
          ? "Sem permissão para este tipo"
          : sanitizeText(value),
    },
    {
      field: "createdAt",
      headerName: "Data de cadastro",
      minWidth: 130,
      valueGetter: (_, row) =>
        getSafeDate([
          row?.createdAt,
          row?.created_at,
          row?.data_abertura,
          row?.data_criacao,
        ]),
    },
    {
      field: "lastUpdate",
      headerName: "Última atualização",
      minWidth: 150,
      valueGetter: (_, row) =>
        getSafeDate([
          row?.lastUpdate,
          row?.updatedAt,
          row?.updated_at,
          row?.data_ultima_atualizacao,
          row?.timestamp,
        ]),
    },
    {
      field: "typeName",
      headerName: "Tipo",
      minWidth: 160,
      flex: 0.45,
      valueGetter: (value, row) =>
        row?.typeName || (row?.typeId != null ? typeIdToName[String(row.typeId)] : null) || "—",
      renderCell: (params) => {
        const label =
          params.row?.typeName ||
          (params.row?.typeId != null ? typeIdToName[String(params.row.typeId)] : null) ||
          "—";
        if (!params.row?.restricted) return label;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <LockOutlinedIcon sx={{ fontSize: 16, color: "warning.main", flexShrink: 0 }} />
            <Typography noWrap variant="body2" sx={{ minWidth: 0 }}>
              {label}
            </Typography>
            <Chip
              label="Sem permissão"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.7rem", flexShrink: 0 }}
            />
          </Box>
        );
      },
    },
    {
      field: "subtypesLabel",
      headerName: "Subtipos",
      minWidth: 160,
      flex: 0.4,
      valueGetter: (_, row) => {
        const fromNames = [row?.subtype1Name, row?.subtype2Name, row?.subtype3Name]
          .map((s) => (s != null ? String(s).trim() : ""))
          .filter(Boolean);
        if (fromNames.length) return fromNames.join(" / ");
        const names = [row?.subtype1, row?.subtype2, row?.subtype3]
          .filter((id) => id != null && id !== "")
          .map((id) => typeIdToName[String(id)])
          .filter(Boolean);
        return names.length ? names.join(" / ") : "—";
      },
    },
    {
      field: "statusLabel",
      headerName: "Status",
      width: 140,
      minWidth: 120,
      valueGetter: (value, row) => (row?.restricted ? "—" : value),
    },
    {
      field: "channel",
      headerName: "Canal",
      minWidth: 140,
      flex: 0.4,
      valueGetter: (value, row) => (row?.restricted ? "—" : value),
    },
  ];

  const clientColumns = [
    { field: "id", headerName: "ID", width: 72, minWidth: 56 },
    {
      field: "nome",
      headerName: "Nome",
      minWidth: 220,
      flex: 0.8,
      renderCell: (params) => {
        const { count, hint } = getClientListRelationshipProps(params?.row);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            <Typography noWrap variant="body2" sx={{ minWidth: 0, flex: "0 1 auto" }}>
              {params.value}
            </Typography>
            {count > 0 ? (
              <ClientRelationshipBadge count={count} hint={hint} compact />
            ) : null}
          </Box>
        );
      },
    },
    { field: "email", headerName: "E-mail", minWidth: 200, flex: 1 },
    { field: "cidade", headerName: "Cidade", width: 140 },
    { field: "uf", headerName: "Estado", width: 80 },
  ];

  const flattenConversations = (data) =>
    data?.pages?.flatMap((page) => {
      if (Array.isArray(page?.list)) return page.list;
      if (Array.isArray(page?.data)) return page.data;
      if (Array.isArray(page)) return page;
      return [];
    }) || [];

  const conversationsRel = flattenConversations(conversationsDataRel);
  const conversationsCom = flattenConversations(conversationsDataCom);

  const conversationRowsRel = conversationsRel.map((conv, index) => ({
    ...conv,
    id: conv.id ?? conv.conversationId ?? `conv-rel-${index}`,
  }));
  const conversationRowsCom = conversationsCom.map((conv, index) => ({
    ...conv,
    id: conv.id ?? conv.conversationId ?? `conv-com-${index}`,
  }));

  const sliceConversationRows = (rows, paginationModel) => {
    const start = paginationModel.page * paginationModel.pageSize;
    return rows.slice(start, start + paginationModel.pageSize);
  };

  const conversationPageRowsRel = sliceConversationRows(conversationRowsRel, paginationRel);
  const conversationPageRowsCom = sliceConversationRows(conversationRowsCom, paginationCom);

  const relCountLabel = formatSearchResultsTabCount({
    loaded: conversationsRelFetched,
    count: conversationRowsRel.length,
    hasNextPage: hasNextPageRel,
  });
  const comCountLabel = formatSearchResultsTabCount({
    loaded: conversationsComFetched,
    count: conversationRowsCom.length,
    hasNextPage: hasNextPageCom,
  });

  const rawClients = clientData?.clients;
  const clients =
    (Array.isArray(rawClients)
      ? rawClients
      : Array.isArray(rawClients?.data)
      ? rawClients.data
      : []) || [];

  const clientTotalCount =
    clientData?.pagination?.total ??
    rawClients?.pagination?.total ??
    0;

  const clientsCountLabel = formatSearchResultsTabCount({
    loaded: clientsFetched,
    count: clientTotalCount,
  });

  const clientRows = clients.map((client, index) => ({
    ...client,
    id: client.id ?? `client-${index}`,
  }));

  const isLoading =
    (tabIndex === 0 && loadingConversationsRel && !conversationsDataRel?.pages?.length) ||
    (tabIndex === 1 && loadingConversationsCom && !conversationsDataCom?.pages?.length) ||
    (tabIndex === 2 && loadingClients);

  const responsavelLabel = { fila: "Fila", humanos: "Humanos", chatbots: "ChatBots", agentes: "Agentes de IA" }[tipo_responsavel] || null;
  const hasFilterChips =
    qTrim.length > 0 ||
    filters?.length > 0 ||
    all_period ||
    data_inicio ||
    data_fim ||
    tipo_responsavel ||
    ids_tipos?.length > 0 ||
    ids_subtipo1?.length > 0 ||
    ids_subtipo2?.length > 0 ||
    ids_subtipo3?.length > 0 ||
    ids_origens?.length > 0 ||
    ids_usuarios?.length > 0 ||
    ids_usuarios_interacao?.length > 0 ||
    ids_agentes?.length > 0 ||
    ids_agentes_interacao?.length > 0 ||
    tags?.length > 0 ||
    includeMessageSearchActive;

  const emptyConversationsRel =
    !loadingConversationsRel &&
    !isFetchingNextPageRel &&
    tabIndex === 0 &&
    conversationRowsRel.length === 0 &&
    !hasNextPageRel;
  const emptyConversationsCom =
    !loadingConversationsCom &&
    !isFetchingNextPageCom &&
    tabIndex === 1 &&
    conversationRowsCom.length === 0 &&
    !hasNextPageCom;
  const emptyClients = !loadingClients && tabIndex === 2 && clientRows.length === 0;

  const conversationsTimeoutMessage =
    tabIndex === 0
      ? (conversationsDataRel?.pages || []).map((p) => p?.timeoutMessage).find(Boolean) || null
      : tabIndex === 1
        ? (conversationsDataCom?.pages || []).map((p) => p?.timeoutMessage).find(Boolean) || null
        : null;

  return (
    <Box
      className="tour-search-results-root"
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 0,
        minHeight: "60vh",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? undefined
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 140px)`,
      }}
    >
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : alpha(theme.palette.primary.main, 0.12)}`,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.primary.main, 0.02),
          boxShadow: (theme) =>
            theme.palette.mode === "dark" ? "none" : `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box className="tour-search-results-toolbar" sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "nowrap" }}>
            <Typography variant="h5" sx={{ fontWeight: 600, flexShrink: 0, minWidth: "fit-content" }}>
              Resultados da Busca
            </Typography>
          </Box>

          {hasFilterChips && (
            <>
              <Divider sx={{ borderColor: (theme) => alpha(theme.palette.divider, 0.6) }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.75, display: "block" }}
                >
                  Filtros ativos:
                </Typography>
                <FilterChipsCollapsible gap={1} seeMoreLabel="Ver mais filtros" seeLessLabel="Ver menos">
                {qTrim && (
                  <Chip
                    size="small"
                    label={`Busca: ${qTrim}`}
                    onDelete={() => removeFilter("q")}
                    sx={{
                      fontWeight: 600,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                      color: "primary.main",
                    }}
                  />
                )}
                {filters.map((f) => (
                  <Chip
                    key={f}
                    size="small"
                    label={f}
                    onDelete={() => removeFilter("filters", f)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }}
                  />
                ))}
                {responsavelLabel && (
                  <Chip
                    size="small"
                    label={`Responsável: ${responsavelLabel}`}
                    onDelete={() => removeFilter("tipo_responsavel")}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12) }}
                  />
                )}
                {ids_tipos?.map((id) => (
                  <Chip
                    key={`t-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Tipo ${id}`}
                    onDelete={() => removeFilter("ids_tipos", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }}
                  />
                ))}
                {ids_subtipo1?.map((id) => (
                  <Chip
                    key={`s1-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Subtipo 1: ${id}`}
                    onDelete={() => removeFilter("ids_subtipo1", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {ids_subtipo2?.map((id) => (
                  <Chip
                    key={`s2-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Subtipo 2: ${id}`}
                    onDelete={() => removeFilter("ids_subtipo2", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {ids_subtipo3?.map((id) => (
                  <Chip
                    key={`s3-${id}`}
                    size="small"
                    label={typeIdToName[String(id)] ?? `Subtipo 3: ${id}`}
                    onDelete={() => removeFilter("ids_subtipo3", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {ids_origens?.map((id) => (
                  <Chip
                    key={`o-${id}`}
                    size="small"
                    label={channelIdToName[String(id)] ?? `Canal ${id}`}
                    onDelete={() => removeFilter("ids_origens", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.info.main, 0.08) }}
                  />
                ))}
                {ids_usuarios?.map((id) => (
                  <Chip
                    key={`u-${id}`}
                    size="small"
                    label={userIdToName[String(id)] ?? `Usuário ${id}`}
                    onDelete={() => removeFilter("ids_usuarios", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.success.main, 0.08) }}
                  />
                ))}
                {ids_agentes?.map((id) => (
                  <Chip
                    key={`ag-${id}`}
                    size="small"
                    label={agentIdToName[String(id)] ?? `Agente ${id}`}
                    onDelete={() => removeFilter("ids_agentes", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12) }}
                  />
                ))}
                {ids_usuarios_interacao?.map((id) => (
                  <Chip
                    key={`ui-${id}`}
                    size="small"
                    label={`Interagiu: ${userIdToName[String(id)] ?? id}`}
                    onDelete={() => removeFilter("ids_usuarios_interacao", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1) }}
                  />
                ))}
                {ids_agentes_interacao?.map((id) => (
                  <Chip
                    key={`agi-${id}`}
                    size="small"
                    label={`Interagiu: ${agentIdToName[String(id)] ?? id}`}
                    onDelete={() => removeFilter("ids_agentes_interacao", id)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12) }}
                  />
                ))}
                {tags?.map((tag) => (
                  <Chip
                    key={`tag-${tag}`}
                    size="small"
                    label={tag === TAG_NO_TAG ? "Sem tag" : `Tag: ${tag}`}
                    onDelete={() => removeFilter("tags", tag)}
                    sx={{ fontWeight: 500, bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08) }}
                  />
                ))}
                {all_period ? (
                  <Chip
                    size="small"
                    label="Todo período"
                    title="Inclui solicitações em aberto sem limite de data"
                    onDelete={() => removeFilter("all_period")}
                    sx={{
                      fontWeight: 500,
                      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                    }}
                  />
                ) : (
                  <>
                    {data_inicio && (
                      <Chip
                        size="small"
                        label={`De: ${formatDateBR(data_inicio)}`}
                        onDelete={() => removeFilter("data_inicio")}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                    {data_fim && (
                      <Chip
                        size="small"
                        label={`Até: ${formatDateBR(data_fim)}`}
                        onDelete={() => removeFilter("data_fim")}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                    {(data_inicio || data_fim) && (
                      <Chip
                        size="small"
                        label={
                          date_field === "entrada"
                            ? "Ref.: data de entrada"
                            : "Ref.: última atualização"
                        }
                        onDelete={() => removeFilter("date_field")}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                  </>
                )}
                {includeMessageSearchActive && (
                  <Chip
                    size="small"
                    label="Texto das mensagens"
                    onDelete={
                      include_message_text === "1" ||
                      include_message_text === "true" ||
                      include_message_text === "sim"
                        ? () => removeFilter("include_message_text")
                        : undefined
                    }
                    sx={{ fontWeight: 500 }}
                  />
                )}
                </FilterChipsCollapsible>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {isLoading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <Tabs
        className="tour-search-results-tabs"
        value={tabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ minHeight: 40, alignSelf: "flex-start", borderBottom: 1, borderColor: "divider", mb: 0 }}
      >
        <Tab
          label={<SearchResultsTabLabel name="Atendimento" countLabel={relCountLabel} />}
          sx={{ textTransform: "none", fontWeight: 500 }}
        />
        <Tab
          label={<SearchResultsTabLabel name="Compras" countLabel={comCountLabel} />}
          sx={{ textTransform: "none", fontWeight: 500 }}
        />
        <Tab
          label={<SearchResultsTabLabel name="Clientes" countLabel={clientsCountLabel} />}
          sx={{ textTransform: "none", fontWeight: 500 }}
        />
      </Tabs>

      {all_period ? (
        <Alert severity="warning" sx={{ mt: 1.5, mb: 0 }}>
          Todo período ativo: a busca inclui solicitações em aberto sem limite de data. Em bases
          com volume grande isso pode deixar a tela mais lenta — refine com outros filtros se
          precisar.
        </Alert>
      ) : null}

      {conversationsTimeoutMessage ? (
        <Alert severity="warning" sx={{ mt: 1.5, mb: 0 }}>
          {conversationsTimeoutMessage}
        </Alert>
      ) : null}

      {isLoading && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} variant="text" width="100%" height={52} sx={{ my: 0.5 }} />
          ))}
        </Paper>
      )}

      {!isLoading && tabIndex === 0 && (
        <>
          {emptyConversationsRel ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Nenhum resultado em Atendimento.</Typography>
            </Paper>
          ) : (
            renderConversationGrid({
              currentPageRows: conversationPageRowsRel,
              allLoadedRows: conversationRowsRel,
              paginationModel: paginationRel,
              onPaginationModelChange: (model) => {
                setPaginationRel(model);
                setSelectedConversationIds([]);
              },
              loadedCount: conversationRowsRel.length,
              hasNextPage: hasNextPageRel,
              isFetchingNextPage: isFetchingNextPageRel,
              exportFileName: "atendimento_busca",
            })
          )}
        </>
      )}

      {!isLoading && tabIndex === 1 && (
        <>
          {emptyConversationsCom ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Nenhum resultado em Compras.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Solicitações comerciais aparecem no Pipeline de vendas.
              </Typography>
            </Paper>
          ) : (
            renderConversationGrid({
              currentPageRows: conversationPageRowsCom,
              allLoadedRows: conversationRowsCom,
              paginationModel: paginationCom,
              onPaginationModelChange: (model) => {
                setPaginationCom(model);
                setSelectedConversationIds([]);
              },
              loadedCount: conversationRowsCom.length,
              hasNextPage: hasNextPageCom,
              isFetchingNextPage: isFetchingNextPageCom,
              exportFileName: "compras_busca",
              rowNavigatePath: (id) => `/sales-pipeline/${id}`,
            })
          )}
        </>
      )}

      {!isLoading && tabIndex === 2 && (
        <>
          {emptyClients ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Nenhum cliente encontrado.</Typography>
            </Paper>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ flex: 1, minHeight: 400 }}>
              <DataGrid
                rows={clientRows}
                columns={clientColumns}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={[10, 24, 50]}
                onRowClick={(params) => navigate(`/clients/${params.row.id}`)}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": { py: 1 },
                  "& .MuiDataGrid-columnHeaders": { minHeight: 44 },
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                }}
              />
            </Paper>
          )}
        </>
      )}

      {canBulk && (
        <>
          <MergeRequestsDialog
            open={mergeOpen}
            selectedIds={selectedConversationIds}
            onClose={() => setMergeOpen(false)}
            onMerged={() => notifyBulkSuccess("Solicitações unificadas com sucesso.")}
          />
          <DistributeRequestsDialog
            open={distributeOpen}
            selectedIds={selectedConversationIds}
            operators={operatorsData?.users || []}
            onClose={() => setDistributeOpen(false)}
            onDistributed={() => notifyBulkSuccess("Solicitações distribuídas com sucesso.")}
          />
          <BulkChangeRequestsDialog
            open={changeStatusOpen}
            selectedIds={selectedConversationIds}
            onClose={() => setChangeStatusOpen(false)}
            onChanged={() => notifyBulkSuccess("Solicitações alteradas com sucesso.")}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchResults;
